import rolesObj from '../configs/roles';
// eslint-disable-next-line import/prefer-default-export

/**
 * Filters to roles the current user can grant
 *
 * @param {boolean} isInitiativeMode - whether the role check is being performed within an initiative
 * @param {boolean} isSuperAdmin - whether the user is a super admin
 * @param {array} roles - array of all eQual platform roles in the format [{ id: 'role-name', name: 'role name' }]
 * @param {string} initiativeRole - the role of the user within the initiative
 * @param {string} organizationRole - the role of the user within the organization
 *
 * @returns {array} - array of roles the current user can grant in the format [{ id: 'role-name', name: 'role name' }]
 */
export const grantableRoles = ({
  isInitiativeMode,
  isSuperAdmin,
  roles,
  initiativeRole,
  organizationRole,
}) => {
  // If within an initiative, filter roles based on the user's initiative role
  if (isInitiativeMode) {
    // To be deprecated once organizational roles are removed
    if (organizationRole === rolesObj.orgAdmin.name) {
      return roles.filter(
        (r) =>
          (r.name.includes('initiative') && !r.name.includes(rolesObj.initiativeAssignee.name)) ||
          // Show "data assignee" role to super admin only
          (isSuperAdmin && r.name.includes(rolesObj.dataAssignee.name)) ||
          // Show "petition-image-viewer" and "petition-uploader" roles only to super admin only
          (isSuperAdmin &&
            (r.name.includes(rolesObj.petitionImageViewer.name) ||
              r.name.includes(rolesObj.petitionUploader.name) ||
              r.name.includes(rolesObj.reportViewer.name) ||
              r.name.includes(rolesObj.canvassingOrganizationUser.name))) ||
          // Show "auxiliary-task-specialist" role to super admin only
          (isSuperAdmin && r.name.includes(rolesObj.auxiliaryTaskSpecialist.name)),
      );
    }

    // Return filtered roles based on initiative role
    return roles.filter((r) => {
      const stringId = String(r.id);

      // If super admin, show all initiative roles
      if (
        isSuperAdmin &&
        Object.values(rolesObj)
          .filter((role) => role.roleType === 'initiative')
          .some((initiativeRole) => initiativeRole.name === stringId)
      ) {
        return true;
      }

      switch (initiativeRole) {
        case rolesObj.initiativeManager.name:
          return (
            stringId.includes(rolesObj.initiativeManager.name) ||
            stringId.includes(rolesObj.initiativeOrganizer.name)
          );
        case rolesObj.initiativeOrganizer.name:
          return stringId.includes(rolesObj.initiativeOrganizer.name);
        case rolesObj.initiativeAdmin.name:
          return r.name.includes('initiative') || stringId.includes(rolesObj.reportViewer.name);
        case rolesObj.initiativeAssignee.name:
          return false;
        case rolesObj.dataAssignee.name:
          return false;
        case rolesObj.petitionImageViewer.name:
          return false;
        case rolesObj.petitionUploader.name:
          return false;
        case rolesObj.reportViewer.name:
          return stringId.includes(rolesObj.reportViewer.name);
        case rolesObj.electionOfficialAssignee.name:
          return false;
        case rolesObj.electionOfficialAdmin.name:
          return (
            stringId.includes(rolesObj.electionOfficialAdmin.name) ||
            stringId.includes(rolesObj.electionOfficialAssignee.name)
          );
        case rolesObj.auxiliaryTaskSpecialist.name:
          return false;
        case rolesObj.canvassingOrganizationUser.name:
          return (
            stringId.includes(rolesObj.canvassingOrganizationUser.name) ||
            stringId.includes(rolesObj.reportViewer.name) ||
            stringId.includes(rolesObj.petitionUploader.name)
          );
        default:
          return false;
      }
    });
  }

  // If super admin and not initiative mode, show only non-initiative admin roles
  if (isSuperAdmin) {
    return roles.filter(
      (r) =>
        r.name.includes('admin') &&
        !Object.values(rolesObj)
          .filter((role) => role.roleType === 'initiative')
          .some((initiativeRole) => initiativeRole.name === r.id),
    );
  }

  return roles.filter((r) => r.name.includes('organization'));
};
